import { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useTheme } from "@/app/providers/ThemeProvider";
import { Moon, Sun, Menu } from "lucide-react";
import { useState } from "react";

const NAV = [
  { to: "/dashboard", label: "Dashboard" },
  { to: "/subjects", label: "Subjects" },
  { to: "/homework", label: "Homework" },
  { to: "/progress", label: "Progress" },
  { to: "/bookmarks", label: "Bookmarks" },
  { to: "/history", label: "History" },
  { to: "/settings", label: "Settings" },
];

/**
 * Shared chrome for every page except Landing/Classroom (those own their
 * full viewport for immersion). Responsive: sidebar on desktop collapses
 * to a bottom-anchored sheet trigger on mobile, per master plan §8.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const { theme, toggle } = useTheme();
  const { pathname } = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  return (
    <div className="min-h-screen">
      <header className="glass sticky top-0 z-20 flex items-center justify-between px-4 py-3 sm:px-6">
        <Link to="/" className="font-display text-lg font-semibold tracking-tight">
          Soren <span className="text-marker">Classroom</span>
        </Link>
        <nav className="hidden md:flex items-center gap-1">
          {NAV.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={cn(
                "rounded-xl px-3 py-2 text-sm font-body transition-colors",
                pathname === item.to ? "bg-marker/10 text-marker" : "text-ink/70 hover:text-ink"
              )}
            >
              {item.label}
            </Link>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <button
            onClick={toggle}
            aria-label="Toggle theme"
            className="rounded-xl p-2 text-ink/70 hover:text-ink"
          >
            {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <button
            onClick={() => setMobileNavOpen((v) => !v)}
            aria-label="Open menu"
            className="rounded-xl p-2 text-ink/70 hover:text-ink md:hidden"
          >
            <Menu size={20} />
          </button>
        </div>
      </header>

      {mobileNavOpen && (
        <div className="glass mx-3 mt-2 rounded-2xl p-2 md:hidden">
          {NAV.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              onClick={() => setMobileNavOpen(false)}
              className="block rounded-xl px-3 py-3 text-sm font-body text-ink/80"
            >
              {item.label}
            </Link>
          ))}
        </div>
      )}

      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6">{children}</main>
    </div>
  );
}
